import { catalog, plural } from '@nolens/i18n';

/** Editor do guia na web (interface, na língua de quem a usa). */
export const guideUiText = catalog(
  {
    space: 'Space',
    untitled: 'Untitled',
    guideTitle: 'Guide title',
    guideDescription: 'Guide description',
    descriptionPlaceholder: 'What this guide is for (optional)',
    meta: (steps: number, min: number) =>
      `${steps} ${plural('en', steps, { one: 'step', other: 'steps' })} · about ${min} min`,
    saved: (time: string) => ` · ≈ ${time} saved documenting (estimate)`,
    guideLanguage: 'Guide language',
    guideTitleTip:
      'Open the app and follow the guide step by step, with the right element highlighted (needs the extension)',
    guide: '▶ Guide me',
    runTip:
      'The extension does the steps marked “The extension does it” on its own; for the others, it guides you',
    run: '⚡ Run',
    guideOnMac: '▶ Guide me on the Mac',
    guideOnMacTip: 'Opens the Mac app, which highlights each step in the right app',
    runOnMac: '⚡ Run on the Mac',
    runOnMacTip:
      'The Mac app does the steps marked “The extension does it” and guides you through the others',
    installMacApp:
      'This guide was recorded on a Mac: to follow it, install and connect the Mac app.',
    share: 'Share',
    export: 'Export',
    exporting: 'Exporting…',
    exportingProgress: (done: number, total: number) => `Exporting… ${done} of ${total}`,
    pdf: 'PDF (print)',
    suggest: '✨ Suggest texts',
    followTip: 'Get a notification when this guide is changed or commented on',
    following: '✓ Notifications on',
    follow: '🔔 Get notifications',
    delete: 'Delete',
    conflict: 'This guide was changed elsewhere while you were editing.',
    conflictHint:
      'Your changes are still here. Save them as a copy of the guide, or discard them and see the latest version.',
    saveAsCopy: 'Save as a copy',
    discardMine: 'Discard my changes',
    copyTitle: (title: string) => `${title || 'Untitled'} (copy)`,
    savedAsCopy: 'Saved as a copy. The original is unchanged.',
    couldNotCopy: "Couldn't save the copy.",
    restored: "We recovered the changes to this guide that hadn't been saved yet.",
    leaveTitle: 'Leave without saving?',
    leaveHint: "Your changes to this guide haven't been saved. If you leave, they'll be lost.",
    stay: 'Stay',
    leaveWithoutSaving: 'Leave without saving',
    noSteps: 'This guide has no steps.',
    comments: 'Comments',
    unsaved: 'Unsaved changes',
    discard: 'Discard',
    save: 'Save',
    cancel: 'Cancel',
    deleteTitle: 'Delete this guide?',
    deleteHint: 'The guide and its screenshots are deleted from the account for all members.',
    // Avisos
    installExtension: 'To guide on the page, install the Nolens Guides extension.',
    saveFirst: 'Save your changes first: the extension uses the saved version.',
    couldNotStart: "Couldn't start the guide.",
    couldNotExport: "Couldn't export.",
    retry: 'Try again',
    missingImagesTitle: (n: number) =>
      `${n} ${plural('en', n, { one: "screenshot didn't load", other: "screenshots didn't load" })}`,
    missingImagesHint: (n: number, total: number) =>
      `${n} of ${total} screenshots couldn't be loaded (no connection, or the session ended). Try again, or export without ${plural('en', n, { one: 'it: that step', other: 'them: those steps' })} will have text only.`,
    exportWithoutImages: 'Export anyway',
    retryExport: 'Try again',
    changesSaved: 'Changes saved.',
    blurApplied: 'Blur applied to the screenshot. Save to keep the rest.',
    suggestionsApplied: 'Suggestions applied. Review and save.',
    changedElsewhere:
      'This guide was changed elsewhere: choose what to do with your changes (above the steps) before changing the image.',
    couldNotBlur: "Couldn't blur the image.",
    replaceImage: 'Replace image',
    replaceImageHint:
      'Use an image from your computer (PNG, JPEG or WebP) instead of this screenshot.',
    imageReplaced: 'Image replaced. Save to keep the rest.',
    couldNotReplace: "Couldn't replace the image.",
    notAnImage: "This file isn't an image that can be opened (use PNG, JPEG or WebP).",
    blurHint:
      "Drag over what you want to hide. When you save, the blur goes into the account's image and can't be undone.",
    // Partilhar
    shareHint: 'Who can open this guide.',
    anyoneWithLink: 'Anyone with the link',
    anyoneWithLinkHint: 'Read only, no account. Turning it off invalidates the link right away.',
    publicLink: 'Public link',
    copy: 'Copy',
    linkCopied: 'Link copied.',
    embed: 'Embed in a page (intranet, wiki, Notion…)',
    teamSpaceHint: 'In a team space, all members see the guide.',
    // Sugestões por IA
    suggestTitle: 'Suggest texts',
    suggestHint:
      'The title, the description and the step titles and descriptions are proposed by AI (Claude, by Anthropic). Only the step text is sent: never screenshots, full addresses or what was typed into fields. Review the descriptions before applying.',
    apply: 'Apply',
    thinking: 'Thinking…',
    aiNotConfigured: 'AI suggestions are not active in this installation yet.',
    titleLabel: 'Title:',
    descriptionLabel: 'Description:',
    stepTitles: (n: number) =>
      `Step titles (${n} ${plural('en', n, { one: 'change', other: 'changes' })})`,
    stepDescriptions: (n: number) => `Step descriptions (${n} new, only where there were none)`,
    // Passos
    showWhole: 'Show the whole screenshot',
    zoomClick: 'Zoom in on the click',
    zoomAllLabel: 'Screenshots',
    zoomAllOn: 'Zoomed in',
    zoomAllOff: 'Whole',
    croppedKept: (n: number) => `Cropped screenshots keep their crop (${n})`,
    editImage: (n: number | undefined) => `Edit the image of step ${n ?? ''}`.trim(),
    moveUp: (n: number | undefined) => (n ? `Move step ${n} up` : 'Move the block up'),
    moveDown: (n: number | undefined) => (n ? `Move step ${n} down` : 'Move the block down'),
    deleteItem: (n: number | undefined) => (n ? `Delete step ${n}` : 'Delete the block'),
    blockTitle: 'Block title',
    blockText: 'Block text',
    textOptional: 'Text (optional)',
    stepTitle: (n: number) => `Step ${n} title`,
    stepNote: (n: number) => `Step ${n} note`,
    notePlaceholder: 'Note for whoever follows the guide (optional)',
    insertBlock: 'Insert block',
    insertHere: 'Insert a text block here',
    insertTip: 'Insert a tip, warning, note or section',
    close: 'Close',
  },
  {
    'pt-PT': {
      space: 'Espaço',
      untitled: 'Sem título',
      guideTitle: 'Título do guia',
      guideDescription: 'Descrição do guia',
      descriptionPlaceholder: 'Para que serve este guia (opcional)',
      meta: (steps, min) =>
        `${steps} ${plural('pt-PT', steps, { one: 'passo', other: 'passos' })} · cerca de ${min} min`,
      saved: (time) => ` · ≈ ${time} poupados a documentar (estimativa)`,
      guideLanguage: 'Língua do guia',
      guideTitleTip:
        'Abrir a aplicação e seguir o guia passo a passo, com o elemento certo realçado (precisa da extensão)',
      guide: '▶ Guiar',
      runTip: 'A extensão faz sozinha os passos marcados «A extensão faz»; nos outros, guia-o',
      run: '⚡ Executar',
      guideOnMac: '▶ Guiar no Mac',
      guideOnMacTip: 'Abre a app de Mac, que realça cada passo na aplicação certa',
      runOnMac: '⚡ Executar no Mac',
      runOnMacTip: 'A app de Mac faz os passos marcados «A extensão faz» e guia-o nos outros',
      installMacApp: 'Este guia foi gravado num Mac: para o seguir, instale e ligue a app de Mac.',
      share: 'Partilhar',
      export: 'Exportar',
      exporting: 'A exportar…',
      exportingProgress: (done, total) => `A exportar… ${done} de ${total}`,
      pdf: 'PDF (imprimir)',
      suggest: '✨ Sugerir textos',
      followTip: 'Receber um aviso quando este guia for alterado ou comentado',
      following: '✓ Recebe avisos',
      follow: '🔔 Receber avisos',
      delete: 'Apagar',
      conflict: 'Este guia foi alterado noutro sítio enquanto editava.',
      conflictHint:
        'As suas alterações continuam aqui. Pode guardá-las numa cópia do guia, ou descartá-las e ver a versão mais recente.',
      saveAsCopy: 'Guardar como cópia',
      discardMine: 'Descartar as minhas alterações',
      copyTitle: (title) => `${title || 'Sem título'} (cópia)`,
      savedAsCopy: 'Guardado como cópia. O original ficou como estava.',
      couldNotCopy: 'Não foi possível guardar a cópia.',
      restored: 'Recuperámos as alterações a este guia que ainda não tinham sido guardadas.',
      leaveTitle: 'Sair sem guardar?',
      leaveHint: 'As alterações a este guia ainda não foram guardadas. Se sair, perdem-se.',
      stay: 'Ficar',
      leaveWithoutSaving: 'Sair sem guardar',
      noSteps: 'Este guia não tem passos.',
      comments: 'Comentários',
      unsaved: 'Alterações por guardar',
      discard: 'Descartar',
      save: 'Guardar',
      cancel: 'Cancelar',
      deleteTitle: 'Apagar este guia?',
      deleteHint: 'O guia e as capturas são apagados da conta para todos os membros.',
      installExtension: 'Para guiar na página, instale a extensão Nolens Guides.',
      saveFirst: 'Guarde as alterações primeiro: a extensão usa a versão guardada.',
      couldNotStart: 'Não foi possível iniciar o guia.',
      couldNotExport: 'Não foi possível exportar.',
      retry: 'Tentar novamente',
      missingImagesTitle: (n) =>
        `${n} ${plural('pt-PT', n, { one: 'captura não carregou', other: 'capturas não carregaram' })}`,
      missingImagesHint: (n, total) =>
        `Não foi possível carregar ${n} de ${total} capturas (sem rede, ou a sessão terminou). Tente outra vez, ou exporte sem ${plural('pt-PT', n, { one: 'ela: esse passo fica', other: 'elas: esses passos ficam' })} só com o texto.`,
      exportWithoutImages: 'Exportar mesmo assim',
      retryExport: 'Tentar outra vez',
      changesSaved: 'Alterações guardadas.',
      blurApplied: 'Desfoque aplicado à captura. Guarde para manter o resto.',
      suggestionsApplied: 'Sugestões aplicadas. Reveja e guarde.',
      changedElsewhere:
        'Este guia foi alterado noutro sítio: escolha o que fazer às suas alterações (por cima dos passos) antes de alterar a imagem.',
      couldNotBlur: 'Não foi possível desfocar a imagem.',
      replaceImage: 'Substituir imagem',
      replaceImageHint: 'Usar uma imagem do computador (PNG, JPEG ou WebP) em vez desta captura.',
      imageReplaced: 'Imagem substituída. Guarde para manter o resto.',
      couldNotReplace: 'Não foi possível substituir a imagem.',
      notAnImage: 'Este ficheiro não é uma imagem que se consiga abrir (use PNG, JPEG ou WebP).',
      blurHint:
        'Arraste sobre o que quer esconder. Ao guardar, o desfoque fica na imagem da conta e não se pode desfazer.',
      shareHint: 'Quem pode abrir este guia.',
      anyoneWithLink: 'Qualquer pessoa com a ligação',
      anyoneWithLinkHint: 'Só leitura, sem conta. Desligar invalida a ligação de imediato.',
      publicLink: 'Ligação pública',
      copy: 'Copiar',
      linkCopied: 'Ligação copiada.',
      embed: 'Incorporar numa página (intranet, wiki, Notion…)',
      teamSpaceHint: 'Num espaço de equipa, todos os membros veem o guia.',
      suggestTitle: 'Sugerir textos',
      suggestHint:
        'O título, a descrição e os títulos e descrições dos passos são propostos por IA (Claude, da Anthropic). Só vai o texto dos passos: nunca capturas, endereços completos ou o que foi escrito em campos. Reveja as descrições antes de aplicar.',
      apply: 'Aplicar',
      thinking: 'A pensar…',
      aiNotConfigured: 'As sugestões por IA ainda não estão ativas nesta instalação.',
      titleLabel: 'Título:',
      descriptionLabel: 'Descrição:',
      stepTitles: (n) =>
        `Títulos dos passos (${n} ${plural('pt-PT', n, { one: 'alteração', other: 'alterações' })})`,
      stepDescriptions: (n) =>
        `Descrições dos passos (${n} ${plural('pt-PT', n, { one: 'nova', other: 'novas' })}, só onde não havia)`,
      showWhole: 'Ver a captura inteira',
      zoomClick: 'Aproximar no clique',
      zoomAllLabel: 'Capturas',
      zoomAllOn: 'Com zoom',
      zoomAllOff: 'Inteiras',
      croppedKept: (n) => `As capturas recortadas mantêm o recorte (${n})`,
      editImage: (n) => `Editar a imagem do passo ${n ?? ''}`.trim(),
      moveUp: (n) => 'Subir ' + (n ?? 'o bloco'),
      moveDown: (n) => 'Descer ' + (n ?? 'o bloco'),
      deleteItem: (n) => 'Apagar ' + (n ? 'o passo ' + n : 'o bloco'),
      blockTitle: 'Título do bloco',
      blockText: 'Texto do bloco',
      textOptional: 'Texto (opcional)',
      stepTitle: (n) => `Título do passo ${n}`,
      stepNote: (n) => `Nota do passo ${n}`,
      notePlaceholder: 'Nota para quem segue o guia (opcional)',
      insertBlock: 'Inserir bloco',
      insertHere: 'Inserir um bloco de texto aqui',
      insertTip: 'Inserir dica, aviso, nota ou secção',
      close: 'Fechar',
    },
  },
);
